/**
 * @file resolveAttachmentSyncChip.ts
 * @description Answers "is my work up, and when did it go?" for one attachment
 * version, as the chip text the panel prints. Consumers: `AttachmentList`'s
 * file row and `AttachmentVersionRow`'s strip entry, both immediately left of
 * the origin tag.
 *
 * Only a version the desktop agent wrote is "synced", and `layout === 'chunked'`
 * is the only honest discriminator for that — `resolveAttachmentVersionOrigin`
 * argues why. A browser upload returns null: it was uploaded, not synced, and
 * the origin tag beside this one already says so. A chunked version whose
 * timestamp is absent or is an unresolved server sentinel also returns null,
 * because a chip carrying a blank or a fabricated time is worse than no chip.
 *
 * The chip is the only date `AttachmentVersionRow` prints for a synced version,
 * so it carries the year whenever the version was not synced this year — a bare
 * `Mar 11` for a 2023 save reads as this year's.
 */

import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';

import type { AttachmentVersion } from '#types/System.types';

export const resolveAttachmentSyncChip = (version: AttachmentVersion): string | null => {
    if (version.layout !== 'chunked') {
        return null;
    }

    const syncedAt = [version.meta?.lastModified, version.meta?.created]
        .find((candidate): candidate is Timestamp => candidate instanceof Timestamp);
    if (!syncedAt) {
        return null;
    }

    const moment = dayjs(syncedAt.toDate());
    const now = dayjs();
    const clockTime = moment.isSame(now, 'day')
        ? moment.format('h:mm A')
        : moment.isSame(now, 'year')
            ? moment.format('MMM D, h:mm A')
            : moment.format('MMM D YYYY, h:mm A');

    return `Synced ${clockTime}`;
};
